import React, { useState } from "react";
import { Card, CardBody, Row, Col, Button } from "reactstrap";
import classnames from "classnames";
import StatsModal from "../Common/StatsModal";
import api from "../../api";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import PvPChallengeModal from "../Modals/PvPChallengeModal";
import "../../Pages/Profile/Profile.css";

const PlayerProfileMainTab = ({
  profile,
  roster,
  level,
  rankings,
  getLevel,
  catcherLevel,
}) => {
  const [challengeSending, setChallengeSending] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  // Get current user from JWT
  const getCurrentUser = () => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        return decoded.sub || decoded.username;
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
    return null;
  };

  const currentUser = getCurrentUser();
  const isOwnProfile = currentUser === profile?.username;

  // Handle battle challenge
  const handleChallenge = async () => {
    if (!profile?.username) return;
    
    setChallengeSending(true);
    try {
      await api.post("/v1/pvp/challenge", {
        opponent_username: profile.username
      }, { withCredentials: true });
      
      toast.success(`Battle challenge sent to ${profile.username}!`, {
        position: "top-center",
        autoClose: 3000
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send challenge";
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 3000
      });
    } finally {
      setChallengeSending(false);
    }
  };
  // Helper for ordinal rank (local to this component)
  const getOrdinalRank = (rank) => {
    const j = rank % 10,
      k = rank % 100;
    if (j === 1 && k !== 11) return `${rank}st`;
    if (j === 2 && k !== 12) return `${rank}nd`;
    if (j === 3 && k !== 13) return `${rank}rd`;
    return `${rank}th`;
  };


    const [statsModal, setStatsModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalData, setModalData] = useState(null);
    const toggleStatsModal = () => setStatsModal(on => !on);


    
   const openStats = async (mon) => {
    setModalLoading(true);
    setStatsModal(true);
    try {
      const { data } = await api.get(`/v1/pokemon/${mon.id}`, { withCredentials: true });
      setModalData(data);
    } catch (err) {
      setModalData({ error: err.response?.data?.detail || "Failed to load" });
    } finally {
      setModalLoading(false);
    }
  };

  

  // getAwardImage now returns the proper image for each rank.
  const getAwardImage = (rank) => {
    if (rank === 1) {
      return (
        <img
          src={`/items/1st.png`}
          alt="1st Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    } else if (rank === 2) {
      return (
        <img
          src={`/items/2nd.png`}
          alt="2nd Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    } else if (rank === 3) {
      return (
        <img
          src={`/items/3rd.png`}
          alt="3rd Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    }
    return null;
  };

  function getCatcherLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 2.5));
    return Math.pow(lvl + 1, 2.5) <= exp ? lvl + 1 : lvl;
  }

  function getMiningLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 2.5));
    return Math.pow(lvl + 1, 2.5) <= exp ? lvl + 1 : lvl;
  }

  // Debug profile data
  console.log("Profile data:", profile);
  console.log("Last active:", profile?.last_active);
  console.log("Is online:", profile?.is_online);

  // Format the last active time in a user-friendly way
  const formatLastActiveTime = (timestamp) => {
    if (!timestamp) return "";
    
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;
    
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    
    // For longer periods, show the actual date
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  return (
    <div>
      <Card className="profile-main-card">
        <CardBody className="profile-main-card__body text-white">
          <Row className="align-items-center">
            <Col md="2" className="text-center">
              <img
                src={`/avatars/${profile?.avatar}.png`}
                alt="Avatar"
                className="profile-avatar"
              />
            </Col>

            <Col md="10">
              <div className="mb-3 profile-header">
                {(() => {
                  // Define staff members and their roles
                  const staffRoles = {
                    'Brokeboy': 'Developer',
                    'ZigCiguzo': 'Developer', 
                    'JoeD2Cold': 'Moderator',
                    'EmoNovember': 'Admin',
                    'Davee': 'Moderator'
                  };
                  
                  const userRole = staffRoles[profile.username];
                  
                  return (
                    <div className="profile-name-row profile-name-row--split">
                      <div className="profile-name-meta">
                        <h3 className="profile-name mb-0">{profile.username}</h3>
                        <span
                          className="profile-role-pill"
                          style={{
                            backgroundColor: userRole
                              ? userRole === "Developer"
                                ? "#E91E63"
                                : userRole === "Admin"
                                  ? "#FF5722"
                                  : "#9C27B0"
                              : "#6c757d",
                          }}
                        >
                          {userRole || "Trainer"}
                        </span>
                        {/* Online Status Indicator */}
                        {profile.is_online && (
                          <span className="profile-online-pill">
                            <span className="profile-online-dot" />
                            ONLINE
                          </span>
                        )}
                      </div>

                      {/* Battle Challenge Button - Only show if online and not own profile */}
                      {!isOwnProfile && profile.is_online && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => setShowChallengeModal(true)}
                          disabled={challengeSending}
                          className="profile-action-button"
                        >
                          {challengeSending ? "Sending..." : "Challenge to Battle"}
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="mb-3 profile-stat-block">
                {/* Currency & Pokédex - Compact Layout */}
                <div className="profile-stat-strip">
                  {/* Coins */}
                  <div className="profile-stat-item">
                    <img src="/items/coin.png" alt="coin" style={{ width: "20px", height: "20px" }} />
                    <strong style={{ color: "#eab308" }}>{profile.coins.toLocaleString()}</strong>
                  </div>

                  {/* Money */}
                  <div className="profile-stat-item">
                    <img src="/items/money.png" alt="money" style={{ width: "20px", height: "20px" }} />
                    <strong style={{ color: "#22c55e" }}>{profile.money.toLocaleString()}</strong>
                  </div>

                  {/* Pokédex */}
                  <div className="profile-stat-item">
                    <img src="/items/pokedex.png" alt="pokedex" style={{ width: "20px", height: "20px" }} />
                    <span style={{ color: "#3b82f6", fontWeight: "600" }}>{profile.pokedex_count.toLocaleString()} Species</span>
                  </div>
                </div>

                {/* Levels in a professional grid */}
                <div className="profile-level-grid">
                  {/* Trainer Level */}
                  <div
                    className="profile-level-card"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.05) 100%)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <img
                      src="/items/battleseek.png"
                      alt="trainer level"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="profile-stat-label">Trainer</span>
                      <span className="profile-stat-value" style={{ color: "#ef4444" }}>Lv. {getLevel(profile.exp).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Catcher Level */}
                  <div
                    className="profile-level-card"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.05) 100%)",
                      border: "1px solid rgba(34, 197, 94, 0.3)",
                    }}
                  >
                    <img
                      src="/items/pokeball.png"
                      alt="catcher level"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="profile-stat-label">Catcher</span>
                      <span className="profile-stat-value" style={{ color: "#22c55e" }}>Lv. {getCatcherLevel(profile.catcher_exp).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Mining Level */}
                  <div
                    className="profile-level-card"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(161, 98, 7, 0.15) 0%, rgba(120, 53, 15, 0.05) 100%)",
                      border: "1px solid rgba(161, 98, 7, 0.3)",
                    }}
                  >
                    <img
                      src="/items/NormalPickaxe.png"
                      alt="mining level"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="profile-stat-label">Mining</span>
                      <span className="profile-stat-value" style={{ color: "#d97706" }}>Lv. {getMiningLevel(profile.miningexp).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Fishing Level */}
                  <div
                    className="profile-level-card"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.05) 100%)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    <img
                      src="/items/superrod.png"
                      alt="fishing level"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="profile-stat-label">Fishing</span>
                      <span className="profile-stat-value" style={{ color: "#3b82f6" }}>Lv. {getMiningLevel(profile.fishingexp || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Farming Level */}
                  <div
                    className="profile-level-card"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%)",
                      border: "1px solid rgba(251, 146, 60, 0.3)",
                    }}
                  >
                    <img
                      src="/items/razzberry.png"
                      alt="farming level"
                      style={{ width: "24px", height: "24px" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span className="profile-stat-label">Farming</span>
                      <span className="profile-stat-value" style={{ color: "#fb923c" }}>Lv. {getMiningLevel(profile.farmingexp || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Last Active Status */}
              {profile.last_active && (
                <div className="mt-3 mb-2">
                  <div
                    className={classnames("profile-status-chip", {
                      "is-online": profile.is_online,
                      "is-offline": !profile.is_online,
                    })}
                  >
                    <span className="profile-status-dot" />
                    <span className="profile-status-text">
                      {profile.is_online ? "Online" : "Offline"}
                    </span>
                    {!profile.is_online && profile.last_active && (
                      <span className="profile-status-time">
                        - {formatLastActiveTime(profile.last_active)}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {rankings ? (
               <div className="profile-leaderboard mt-3">
                 <h5 className="profile-section-title">Leaderboard Rankings</h5>
                 <Row>
                   <Col md="4">
                     Trainer:{" "}
                     {rankings.trainer.rank <= 3
                       ? getAwardImage(rankings.trainer.rank)
                       : getOrdinalRank(rankings.trainer.rank)}
                   </Col>
                   <Col md="4">
                     Catcher:{" "}
                     {rankings.catcher.rank <= 3
                       ? getAwardImage(rankings.catcher.rank)
                       : getOrdinalRank(rankings.catcher.rank)}
                   </Col>
                   <Col md="4">
                     Mining:{" "}
                     {rankings.mining.rank <= 3
                       ? getAwardImage(rankings.mining.rank)
                       : getOrdinalRank(rankings.mining.rank)}
                   </Col>
                 </Row>
               </div>
             ):(
               <div className="profile-loading modern-profile-loading">
                 <img
                   src="/loading.gif"
                   alt="Loading..."
                   width="32"
                 />{" "}
                 Loading rankings...
               </div>
             )
             
             }
            </Col>
          </Row>
        </CardBody>
      </Card>

      <h5 className="profile-section-title">Party</h5>
      <Row>
        {roster.length > 0 ? (
          roster.map((poke) => (
            <Col key={poke.id} md="3" sm="6" className="mb-3">
              <Card
                className="profile-party-card"
                onClick={() => openStats(poke)}
              >
                <CardBody className="profile-party-card__body text-center">
                  <img
                    src={`/sprites/${poke.name}.png`}
                    alt={poke.name}
                    style={{ width: "120px" }}
                  />
                  <h6 className="profile-party-name">
                    {poke.nickname || poke.name}
                  </h6>
                  <div className="profile-party-level">
                    Level: {getLevel(poke.totalexp).toLocaleString()}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))
        ) : (
          <div className="profile-empty-state">
            <i className="ri-ghost-line" style={{ fontSize: "2rem", marginBottom: "0.5rem" }}></i>
            <p>No Pokémon in this roster.</p>
          </div>
        )}
      </Row>
      <StatsModal
              isOpen={statsModal}
              toggle={toggleStatsModal}
              loading={modalLoading}
              data={modalData}
            />
            
      {/* PvP Challenge Modal */}
      {showChallengeModal && (
        <PvPChallengeModal
          onClose={() => setShowChallengeModal(false)}
          targetUsername={profile?.username}
          onChallengeSuccess={() => {
            toast.success(`Battle challenge sent to ${profile?.username}!`, {
              position: "top-center",
              autoClose: 3000
            });
          }}
        />
      )}
    </div>
  );
};

export default PlayerProfileMainTab;
